import { VALIDATION_MESSAGES } from './constants';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const validateField = (value: any, rules: ValidationRule): ValidationResult => {
  // Required validation
  if (rules.required && (!value || value.toString().trim() === '')) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.REQUIRED,
    };
  }

  // Skip other validations if value is empty and not required
  if (!value || value.toString().trim() === '') {
    return { isValid: true };
  }

  const stringValue = value.toString();

  // Min length validation
  if (rules.minLength && stringValue.length < rules.minLength) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.MIN_LENGTH(rules.minLength),
    };
  }

  // Max length validation
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.MAX_LENGTH(rules.maxLength),
    };
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.EMAIL,
    };
  }

  // Custom validation
  if (rules.custom) {
    const customResult = rules.custom(value);
    if (customResult) {
      return {
        isValid: false,
        message: customResult,
      };
    }
  }

  return { isValid: true };
};

export const validateEmail = (email: string): ValidationResult => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return {
    isValid: emailRegex.test(email),
    message: emailRegex.test(email) ? undefined : VALIDATION_MESSAGES.EMAIL,
  };
};

export const validatePassword = (password: string): ValidationResult => {
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long',
    };
  }

  if (!/(?=.*[a-z])/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one lowercase letter',
    };
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one uppercase letter',
    };
  }

  if (!/(?=.*\d)/.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain at least one number',
    };
  }

  return { isValid: true };
};

export const validatePhone = (phone: string): ValidationResult => {
  const phoneRegex = /^\+?[1-9][\d]{0,15}$/;
  const cleanPhone = phone.replace(/\s/g, '');
  return {
    isValid: phoneRegex.test(cleanPhone),
    message: phoneRegex.test(cleanPhone) ? undefined : 'Please enter a valid phone number',
  };
};

export const validateNumber = (value: any): ValidationResult => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.INVALID_NUMBER,
    };
  }
  return { isValid: true };
};

export const validatePositiveNumber = (value: any): ValidationResult => {
  const num = parseFloat(value);
  if (isNaN(num) || num <= 0) {
    return {
      isValid: false,
      message: VALIDATION_MESSAGES.POSITIVE_NUMBER,
    };
  }
  return { isValid: true };
};

export const validateForm = (data: Record<string, any>, rules: Record<string, ValidationRule>): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.keys(rules).forEach((field) => {
    const validation = validateField(data[field], rules[field]);
    if (!validation.isValid && validation.message) {
      errors[field] = validation.message;
    }
  });

  return errors;
}; 